import { useCallback, useReducer, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { ClientError } from 'graphql-request';
import flatten from 'lodash/flatten';
import last from 'lodash/last';
import isEqual from 'lodash/isEqual';
import size from 'lodash/size';

import { LocalForage } from '../../../../../utils/LocalForage';

import {
  FetchItemsGqlQuery,
  FetchItemsFilters,
  FetchItemsSort,
  FetchItemsLimit,
  UUID,
  FetchItemCacheKey,
  FetchItemGqlQuery
} from '../../../../../types';

import {
  ReadInfiniteIndexQueryResponse,
  ReadInfiniteIndexQueryData,
  ReadInfiniteIndexQueryBaseNodeType,
  ReadInfiniteIndexQueryDefaultOptionsOpts
} from './useReadInfiniteIndexQuery.types';

import { useUpdateInfiniteIndexQueryItemCache } from '../useInfiniteIndexQuery/hooks/useUpdateInfiniteIndexQueryItemCache';
import { useAddInfiniteIndexQueryItemCache } from '../useInfiniteIndexQuery/hooks/useAddInfiniteIndexQueryItemCache';

import { parseRequestError } from '../../../../../utils/parseRequestError';

import {
  indexRequestReducer,
  IndexRequestReducerType
} from '../useInfiniteIndexQuery/reducers/indexRequestReducer';

import { fetchReadItems } from '../../actions/fetchReadItems';
import { fetchReadItem } from '../../actions/fetchReadItem';

import { changeItemsFiltersAction } from '../useInfiniteIndexQuery/actions/changeItemsFiltersAction';
import { clearItemsFiltersAction } from '../useInfiniteIndexQuery/actions/clearItemsFiltersAction';
import { filterItemsAction } from '../useInfiniteIndexQuery/actions/filterItemsAction';
import { sortItemsAction } from '../useInfiniteIndexQuery/actions/sortItemsAction';
import { limitItemsAction } from '../useInfiniteIndexQuery/actions/limitItemsAction';

import {
  INITIAL_FILTERS,
  INITIAL_LIMIT,
  INITIAL_PAGE,
  INITIAL_SORT
} from '../useInfiniteIndexQuery/infiniteIndexRequestConstants';

import { retryCustomFunction } from '../useInfiniteIndexQuery/utils/retryCustomFunction';

type ReadInfiniteIndexErrorType = Error | ClientError;

type ReadInfiniteIndexQueryWithFetchItemOptions = {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemGqlQuery;
};

type ReadInfiniteIndexQueryWithoutFetchItemOptions = {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
};

interface ReadInfiniteIndexQueryOptions<NodeType> {
  cacheKey: string;
  query: FetchItemsGqlQuery;
  scope: string;
  initialFilters?: FetchItemsFilters;
  initialSort?: FetchItemsSort;
  initialLimit?: FetchItemsLimit;
  options?: ReadInfiniteIndexQueryDefaultOptionsOpts<NodeType>;
}

function useReadInfiniteIndexQuery<
  NodeType extends ReadInfiniteIndexQueryBaseNodeType
>({
  cacheKey,
  scope,
  query,
  initialFilters = INITIAL_FILTERS,
  initialSort = INITIAL_SORT,
  initialLimit = INITIAL_LIMIT,
  options = {},
  fetchItemCacheKey,
  fetchItemQuery
}: ReadInfiniteIndexQueryOptions<NodeType> &
  (
    | ReadInfiniteIndexQueryWithFetchItemOptions
    | ReadInfiniteIndexQueryWithoutFetchItemOptions
  )) {
  const localForageCacheKey = `${cacheKey}-infinite-index`;

  const [{ currentFilters, currentLimit, currentSort }, dispatch] =
    useReducer<IndexRequestReducerType>(indexRequestReducer, {
      currentLimit: initialLimit,
      currentFilters: initialFilters,
      currentSort: initialSort
    });

  const { data: placeholderData, isFetched: placeholderDataFetched } =
    useInfiniteQuery<ReadInfiniteIndexQueryResponse<NodeType> | null>(
      `${cacheKey}-placeholder`,
      () =>
        LocalForage.getItem<ReadInfiniteIndexQueryResponse<NodeType>>(
          localForageCacheKey
        ),
      {
        enabled: options.enabledPlaceholder
      }
    );

  const currentParams = useMemo(() => {
    return {
      filters: currentFilters,
      sort: currentSort,
      limit: currentLimit
    };
  }, [currentFilters, currentSort, currentLimit]);

  const fullCacheKey = useMemo(() => {
    return [cacheKey, currentParams];
  }, [cacheKey, currentParams]);

  const {
    data,
    isFetched,
    isLoading,
    error,
    isPlaceholderData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery<
    ReadInfiniteIndexQueryResponse<NodeType>,
    ReadInfiniteIndexErrorType
  >(
    fullCacheKey,
    useCallback(
      ({ pageParam = INITIAL_PAGE }) =>
        fetchReadItems({
          query,
          page: pageParam,
          ...currentParams
        }),
      [currentParams, query]
    ),
    {
      enabled:
        options.enabled ||
        (options.enabledPlaceholder && placeholderDataFetched),
      cacheTime: options.cacheTime,
      staleTime: options.staleTime,
      retry: retryCustomFunction,
      onSuccess: useCallback(
        (data) => {
          options.onSuccess?.(data);
          if (
            data?.pages[0] &&
            size(data?.pages) === 1 &&
            isEqual(currentFilters, initialFilters) &&
            isEqual(currentSort, initialSort) &&
            currentLimit === initialLimit
          ) {
            return LocalForage.setItem<
              ReadInfiniteIndexQueryResponse<NodeType>
            >(localForageCacheKey, data.pages[0]);
          }
        },
        [
          currentFilters,
          currentSort,
          currentLimit,
          initialFilters,
          initialSort,
          initialLimit,
          localForageCacheKey,
          options
        ]
      ),
      placeholderData: useCallback(() => {
        if (
          placeholderData?.pages?.[0] &&
          isEqual(currentFilters, initialFilters) &&
          isEqual(currentSort, initialSort) &&
          currentLimit === initialLimit
        ) {
          return placeholderData as ReadInfiniteIndexQueryData<NodeType>;
        }
      }, [
        currentFilters,
        currentSort,
        currentLimit,
        initialFilters,
        initialSort,
        initialLimit,
        placeholderData
      ]),
      getNextPageParam: useCallback(
        (lastPage) => {
          return lastPage?.[scope]?.paginationInfo?.nextPage ?? undefined;
        },
        [scope]
      )
    }
  );

  const loadMoreItems = useCallback(() => fetchNextPage(), [fetchNextPage]);

  const lastQueryResponseValue = last(data?.pages)?.[scope];
  const placeholderResponseValue = placeholderData?.pages?.[0]?.[scope];

  const addItemCache = useAddInfiniteIndexQueryItemCache<NodeType>({
    fullCacheKey,
    scope
  });

  const updateItemCache = useUpdateInfiniteIndexQueryItemCache<NodeType>({
    fullCacheKey,
    scope
  });

  const items = useMemo<NodeType[]>(() => {
    const pagesNodes = data?.pages?.map((page) => page?.[scope]?.nodes);
    return pagesNodes ? flatten(pagesNodes) : [];
  }, [data, scope]);

  const isLoadingTotalCount = isLoading
    ? placeholderResponseValue?.paginationInfo?.totalCount
    : null;

  const queryClient = useQueryClient();

  return {
    data,
    items,
    itemsError: parseRequestError(error),
    itemsTotalCount:
      lastQueryResponseValue?.paginationInfo?.totalCount ||
      isLoadingTotalCount ||
      0,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentFilters,
    currentSort,
    currentPage: lastQueryResponseValue?.paginationInfo?.currentPage,
    currentLimit,
    hasNextPage,
    addItemCache,
    updateItemCache,
    loadMoreItems,
    filterItems: useCallback(
      (nextFilters: FetchItemsFilters) =>
        dispatch(filterItemsAction(nextFilters)),
      [dispatch]
    ),
    changeItemsFilters: useCallback(
      (
        changedFilters: Partial<FetchItemsFilters>,
        removeFilters: string[] = []
      ) => dispatch(changeItemsFiltersAction(changedFilters, removeFilters)),
      [dispatch]
    ),
    clearItemsFilters: useCallback(
      () => dispatch(clearItemsFiltersAction()),
      [dispatch]
    ),
    clearItemsFiltersPersistInitial: useCallback(
      () => dispatch(filterItemsAction(initialFilters)),
      [dispatch, initialFilters]
    ),
    sortItems: useCallback(
      (nextSort: FetchItemsSort) => dispatch(sortItemsAction(nextSort)),
      [dispatch]
    ),
    limitItems: useCallback(
      (nextLimit: FetchItemsLimit) => dispatch(limitItemsAction(nextLimit)),
      [dispatch]
    ),
    prefetchItem: useCallback(
      (itemUuid: UUID) =>
        queryClient.prefetchQuery(
          [fetchItemCacheKey, itemUuid],
          () =>
            fetchReadItem({
              query: fetchItemQuery,
              uuid: itemUuid
            }),
          { staleTime: 5000 }
        ),
      [queryClient, fetchItemCacheKey, fetchItemQuery]
    )
  };
}

export default useReadInfiniteIndexQuery;
